import {
    Box,
    CircularProgress,
    FormControl,
    FormHelperText,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
} from '@mui/material';
import { FC, useState, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import BranchSelectorMenu from './BranchSelectorMenu';
import ClearIcon from '@mui/icons-material/Clear';
import { useDispatch, useSelector } from 'react-redux';
import { selectGetBranchesForDeployStatus } from '@pages/stands/store/selectors';
import { getBranchesForDeployReset } from '@pages/stands/store';
import { StandBranch } from '@pages/stands/types';
import { RequestStatusEnum } from '@shared/types';

interface BranchSelectorProps {
    value: StandBranch;
    onChange: (branch: StandBranch) => void;
    error?: boolean;
    disabled?: boolean;
}

const BranchSelector: FC<BranchSelectorProps> = ({ value, error, disabled, onChange }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const getBranchesForDeployStatus = useSelector(selectGetBranchesForDeployStatus);
    const dispatch = useDispatch();

    const handleSelectOpen = () => {
        dispatch(getBranchesForDeployReset());
        setOpen(true);
    };

    const handleSelectClose = () => {
        dispatch(getBranchesForDeployReset());
        setOpen(false);
    };

    const handleClearBranchSelect = () => {
        onChange('');
    };

    const handleMouseDownClearIcon = (event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    return (
        <FormControl margin="dense" fullWidth size="small" error={error} disabled={disabled}>
            <InputLabel id="branch-select">{t('branch')}</InputLabel>
            <Select
                labelId="branch-select"
                MenuProps={{
                    PaperProps: {
                        sx: {
                            padding: '0px 8px',
                            height: 400,
                            width: 400,
                        },
                    },
                }}
                value={' '}
                renderValue={() => {
                    if (!value.length) {
                        return (
                            <Box
                                sx={{
                                    color: 'var(--text-disabled)',
                                }}
                            >
                                {t('select_branch')}...
                            </Box>
                        );
                    }
                    return value;
                }}
                open={open}
                onOpen={handleSelectOpen}
                onClose={handleSelectClose}
                input={
                    <OutlinedInput
                        sx={{
                            '& .MuiSelect-select': {
                                paddingRight: '0 !important',
                            },
                        }}
                        label={t('branch')}
                        endAdornment={
                            getBranchesForDeployStatus === RequestStatusEnum.Loading ? (
                                <InputAdornment position="start" sx={{ mr: 2 }}>
                                    <CircularProgress size={18} />
                                </InputAdornment>
                            ) : (
                                Boolean(value.length) && (
                                    <InputAdornment position="start" sx={{ mr: 2 }}>
                                        <IconButton
                                            size="small"
                                            onClick={handleClearBranchSelect}
                                            onMouseDown={handleMouseDownClearIcon}
                                        >
                                            <ClearIcon />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            )
                        }
                    />
                }
            >
                {/* hack to disable MUI Select warning */}
                <MenuItem sx={{ position: 'absolute', visibility: 'hidden' }} value={' '} />
                <BranchSelectorMenu onChange={onChange} open={open} setOpen={setOpen} />
            </Select>
            {error && <FormHelperText>{t('specify_branch')}</FormHelperText>}
        </FormControl>
    );
};

export default BranchSelector;
